(() => {
  const formSelector = '[data-product-form][data-freshka-size-required="true"]';
  const rootSelector = '[data-is-full-product], [data-is-quick-view]';
  const states = new WeakMap();

  function failClosed(form) {
    const payment = form.querySelector('[data-freshka-payment-button]');
    if (payment) payment.hidden = true;
    const idInput = form.querySelector('[data-variant-select]');
    if (idInput) idInput.value = '';
    form.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });
    return null;
  }

  function stateFor(form) {
    if (states.has(form)) return states.get(form);
    const root = form.closest(rootSelector) || form;
    const sizeInput = root.querySelector('[data-freshka-size-input]');
    const idInput = form.querySelector('[data-variant-select]');
    const json = form.querySelector('[data-freshka-variants]');
    if (!idInput || !json) return failClosed(form);
    let variants;
    try {
      variants = JSON.parse(json.textContent);
      if (!Array.isArray(variants) || !variants.length || variants.some(variant =>
        !variant || !Number.isSafeInteger(variant.id) || !Array.isArray(variant.options) ||
        !variant.options.length || variant.options.some(value => typeof value !== 'string') ||
        typeof variant.available !== 'boolean')) return failClosed(form);
    } catch {
      return failClosed(form);
    }
    const state = { form, root, sizeInput, idInput, variants };
    states.set(form, state);
    form.noValidate = true;
    if (!sizeInput) {
      idInput.closest('.product-form__variants').style.display = 'block';
      idInput.style.display = 'block';
    }
    return state;
  }

  function currentVariant(state) {
    if (!state.sizeInput) {
      return state.variants.find(variant => String(variant.id) === state.idInput.value) || null;
    }
    const inputs = [...state.root.querySelectorAll('select[name^="options["]')];
    if (!state.sizeInput.value || inputs.some(input => !input.value)) return null;
    return state.variants.find(variant => inputs.every(input => {
      const index = Number(input.dataset.index.replace('option', '')) - 1;
      return variant.options[index] === input.value;
    })) || null;
  }

  function synchronize(state) {
    const variant = currentVariant(state);
    const pending = state.sizeInput ? !state.sizeInput.value : !state.idInput.value;
    const buttons = [...state.form.querySelectorAll('[data-add-to-cart]')];
    state.form.dataset.freshkaSizePending = String(pending);
    if (state.sizeInput) state.idInput.value = variant ? String(variant.id) : '';
    buttons.forEach(button => {
      const label = pending ? button.dataset.langChooseSize : variant ?
        (variant.available ? button.dataset.langAvailable : button.dataset.langSoldOut) : button.dataset.langUnavailable;
      button.disabled = !pending && (!variant || !variant.available);
      button.setAttribute('aria-disabled', String(button.disabled));
      button.setAttribute('aria-label', label);
      const text = button.querySelector('[data-add-to-cart-text]');
      if (text && text.textContent !== label) text.textContent = label;
    });
    const payment = state.form.querySelector('[data-freshka-payment-button]');
    if (payment) payment.hidden = pending || !variant || !variant.available;
    const sticky = state.form.querySelector('.sticky-atc-bar__meta-options');
    const label = pending ? buttons[0]?.dataset.langChooseSize : variant?.options.join(', ');
    if (sticky && label && sticky.textContent !== label) sticky.textContent = label;
    if (state.sizeInput) {
      const group = state.sizeInput.closest('.product__option');
      const selectedLabel = group?.querySelector('[data-selected-value-for-option]');
      const text = state.sizeInput.value || buttons[0]?.dataset.langChooseSize;
      if (selectedLabel && selectedLabel.textContent !== text) selectedLabel.textContent = text;
      group?.querySelectorAll('[data-button]').forEach(button => {
        const selected = button.dataset.optionValue === state.sizeInput.value;
        button.classList.toggle('selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }
    if (!pending) {
      const message = state.form.querySelector('[data-freshka-size-message]');
      if (message) message.hidden = true;
      state.sizeInput?.removeAttribute('aria-invalid');
    }
    return { variant, pending };
  }

  function promptForSize(state) {
    const message = state.form.querySelector('[data-freshka-size-message]');
    if (message) message.hidden = false;
    state.sizeInput?.setAttribute('aria-invalid', 'true');
    const group = state.sizeInput?.closest('.product__option');
    const target = group?.querySelector('[data-button]') || state.sizeInput || state.idInput;
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
  }

  function block(event, form) {
    const state = stateFor(form);
    if (!state) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return true;
    }
    const { variant, pending } = synchronize(state);
    if (pending || !variant || !variant.available) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pending) promptForSize(state);
      return true;
    }
    return false;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-add-to-cart], [data-freshka-payment-button]');
    const form = button?.closest(formSelector);
    if (form) block(event, form);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches(formSelector) || block(event, form)) return;
    const state = stateFor(form);
    if (!state.sizeInput) {
      event.preventDefault();
      event.stopImmediatePropagation();
      HTMLFormElement.prototype.submit.call(form);
    }
  }, true);

  document.addEventListener('change', event => {
    const root = event.target.closest(rootSelector);
    if (!root) return;
    root.querySelectorAll(formSelector).forEach(form => {
      const state = stateFor(form);
      if (state) {
        const { variant, pending } = synchronize(state);
        if (pending || !variant) event.stopImmediatePropagation();
        queueMicrotask(() => synchronize(state));
      }
    });
  }, true);

  function initialize(node) {
    if (!(node instanceof Element) && node !== document) return;
    const forms = [...node.querySelectorAll(formSelector)];
    if (node.matches?.(formSelector)) forms.unshift(node);
    forms.forEach(form => {
      if (!states.has(form)) {
        const state = stateFor(form);
        if (state) synchronize(state);
      }
    });
  }

  initialize(document);
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(initialize)))
    .observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', () => document.querySelectorAll(formSelector).forEach(form => {
    const state = stateFor(form);
    if (state) synchronize(state);
  }));
})();
